import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Card, Field, Input, Select, Textarea, StatusChip, Tag, Notice, ErrorSummary, DemoBadge } from "../components/kit";
import { ConfirmDialog } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { showSimulated } from "../lib/demo";
import { api, newClientId, parseError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtDateTime, fmtLocal } from "../lib/format";
import { TIME_ZONES } from "./Profile";

export function VisitsSection({ d, reload }) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const prog = d.programme;
  const [f, setF] = useState({ scheduled_local: "", time_zone: me.user.time_zone || "UTC", objective: "", preliminary_plan: "", retrospective: false, actual_date: "", actual_date_precision: "day" });
  const [cid, setCid] = useState(newClientId());
  const [apiErr, setApiErr] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const schedule = async () => {
    setApiErr(null);
    try {
      const body = { ...f, programme_id: prog.id, client_id: cid };
      if (f.retrospective && !f.scheduled_local) body.scheduled_local = `${f.actual_date.length === 4 ? f.actual_date + "-01-01" : f.actual_date.length === 7 ? f.actual_date + "-01" : f.actual_date}T00:00`;
      await api.post("/visits", body);
      setCid(newClientId());
      toast.success(t("visit.scheduled_ok"));
      reload();
    } catch (e) { setApiErr(parseError(e)); }
  };
  return (
    <Card data-testid="visits-section">
      <h2 className="mb-3 text-lg font-bold">{t("visit.title_list")}</h2>
      <ul className="mb-4 space-y-2">{d.visits.map((v) => (
        <li key={v.id}><Link to={`/visits/${v.id}`} data-testid={`visit-row-${v.number}`} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm hover:bg-muted">
          <b>{v.number}</b>{v.is_demo && <DemoBadge short />}<span>{fmtLocal(v.scheduled_local, v.time_zone)}</span>{v.retrospective && <Tag>{t("visit.retrospective")}</Tag>}
          <span className="text-muted-foreground">{t("programme.edition_n", { n: v.programme_edition })}</span><span className="ml-auto"><StatusChip status={v.status} /></span>
        </Link></li>))}</ul>
      {prog.status === "active" && (
        <details className="rounded-2xl border bg-white p-4" data-testid="schedule-visit-form">
          <summary className="cursor-pointer font-semibold">{t("visit.schedule")}</summary>
          <ErrorSummary errors={apiErr?.errors} />
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="flex min-h-[44px] items-center gap-2 text-sm font-semibold sm:col-span-2"><input type="checkbox" className="h-5 w-5" data-testid="visit-retrospective-checkbox" checked={f.retrospective} onChange={set("retrospective")} />{t("visit.retrospective_entry")}</label>
            {!f.retrospective && <Field label={t("visit.date_time")} htmlFor="v-dt"><Input id="v-dt" type="datetime-local" data-testid="visit-datetime-input" value={f.scheduled_local} onChange={set("scheduled_local")} /></Field>}
            {f.retrospective && <>
              <Field label={t("field.date_precision")} htmlFor="v-prec"><Select id="v-prec" data-testid="visit-precision-select" value={f.actual_date_precision} onChange={(e) => setF({ ...f, actual_date_precision: e.target.value, actual_date: "" })}>{["day", "month", "year"].map((x) => <option key={x} value={x}>{t(`precision.${x}`)}</option>)}</Select></Field>
              <Field label={t("visit.actual_date")} htmlFor="v-ad"><Input id="v-ad" type={f.actual_date_precision === "day" ? "date" : f.actual_date_precision === "month" ? "month" : "number"} data-testid="visit-actual-date-input" value={f.actual_date} onChange={set("actual_date")} /></Field>
            </>}
            <Field label={t("field.time_zone")} htmlFor="v-tz"><Select id="v-tz" data-testid="visit-timezone-select" value={f.time_zone} onChange={set("time_zone")}>{TIME_ZONES.map((z) => <option key={z}>{z}</option>)}</Select></Field>
            <Field label={t("field.objective")} htmlFor="v-obj"><Input id="v-obj" data-testid="visit-objective-input" value={f.objective} onChange={set("objective")} /></Field>
            <Field label={t("visit.preliminary_plan")} htmlFor="v-pp"><Input id="v-pp" data-testid="visit-plan-input" value={f.preliminary_plan} onChange={set("preliminary_plan")} /></Field>
          </div>
          <Btn className="mt-3" testid="visit-schedule-btn" onClick={schedule}>{t("visit.schedule")}</Btn>
        </details>
      )}
    </Card>
  );
}

export function HomeTasksSection({ d, reload }) {
  const { t } = useTranslation();
  const prog = d.programme;
  const [f, setF] = useState({ key: "", deadline: "", reporting_method: "" });
  const [cid, setCid] = useState(newClientId());
  const [apiErr, setApiErr] = useState(null);
  const [replace, setReplace] = useState(null);
  const [cancel, setCancel] = useState(null);
  const acts = prog.stages.flatMap((s) => s.home_activities.map((h) => ({ s, h })));
  const create = async () => {
    const [stage_id, home_activity_id] = f.key.split("|");
    try {
      await api.post(`/programmes/${prog.id}/home-tasks`, { stage_id, home_activity_id, deadline: f.deadline, reporting_method: f.reporting_method, client_id: cid });
      setCid(newClientId());
      setApiErr(null);
      reload();
    } catch (e) { setApiErr(parseError(e)); }
  };
  return (
    <Card data-testid="home-tasks-section">
      <h2 className="mb-3 text-lg font-bold">{t("task.title_list")}</h2>
      <ul className="mb-4 space-y-2">{d.home_tasks.map((x) => (
        <li key={x.id} data-testid={`home-task-${x.number}-v${x.version}`} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
          <Link to={`/tasks/${x.id}`} className="font-semibold underline-offset-4 hover:underline">{x.number} · {t("common.version_n", { n: x.version })}</Link><span>{x.title}</span>
          <span className="text-muted-foreground">{x.protocol_id ? t("home_field.standard_block") : t(`lang.${x.text_language}`)}</span>
          <StatusChip status={x.status} />{x.change_reason && <span className="text-xs text-muted-foreground">{t("common.reason")}: {x.change_reason}</span>}
          {["draft", "sent"].includes(x.status) && <span className="ml-auto flex gap-2">
            <Btn variant="outline" className="min-h-[40px]" testid={`replace-task-btn-${x.number}`} onClick={() => setReplace(x)}>{t("task.replace")}</Btn>
            <Btn variant="danger" className="min-h-[40px]" testid={`cancel-task-btn-${x.number}`} onClick={() => setCancel(x)}>{t("task.cancel")}</Btn>
          </span>}
        </li>))}</ul>
      {prog.status === "active" && acts.length > 0 && (
        <div className="grid gap-3 rounded-2xl border bg-white p-4 sm:grid-cols-3" data-testid="create-task-form">
          <ErrorSummary errors={apiErr?.errors} />
          <Field label={t("task.from_activity")} htmlFor="t-act"><Select id="t-act" data-testid="task-activity-select" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })}>
            <option value="">{t("common.choose")}</option>{acts.map(({ s, h }) => <option key={h.id} value={`${s.id}|${h.id}`}>{h.title || h.id}</option>)}</Select></Field>
          <Field label={t("task.deadline")} htmlFor="t-dl"><Input id="t-dl" type="date" data-testid="task-deadline-input" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} /></Field>
          <Field label={t("task.reporting_method")} htmlFor="t-rm"><Input id="t-rm" data-testid="task-reporting-input" value={f.reporting_method} onChange={(e) => setF({ ...f, reporting_method: e.target.value })} /></Field>
          <Btn variant="soft" disabled={!f.key} testid="task-create-btn" onClick={create}>{t("task.create")}</Btn>
        </div>
      )}
      <ReplaceDialog task={replace} onClose={() => setReplace(null)} reload={reload} />
      <ConfirmDialog open={!!cancel} onOpenChange={() => setCancel(null)} title={t("task.cancel_title")} consequence={t("task.cancel_consequence")} reasonLabel={t("common.reason")} reasonRequired danger
        confirmLabel={t("task.cancel")} testid="cancel-task-dialog" onConfirm={(reason) => api.post(`/home-tasks/${cancel.id}/cancel`, { reason }).then(reload)} />
    </Card>
  );
}

function ReplaceDialog({ task, onClose, reload }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [stop, setStop] = useState("");
  return (
    <ConfirmDialog open={!!task} onOpenChange={onClose} title={t("task.replace_title")} consequence={t("task.replace_consequence")} reasonLabel={t("common.reason")} reasonRequired
      confirmLabel={t("task.replace")} testid="replace-task-dialog"
      onConfirm={(reason) => api.post(`/home-tasks/${task.id}/replace`, { reason, client_id: newClientId(), changes: { ...(text ? { text } : {}), ...(stop ? { stopping_conditions: stop } : {}) } }).then(() => { setText(""); setStop(""); reload(); })}>
      {task && !task.protocol_id && <Field label={t("home_field.text")} htmlFor="rp-text"><Textarea id="rp-text" data-testid="replace-text-input" value={text} placeholder={task.text} onChange={(e) => setText(e.target.value)} /></Field>}
      <Field label={t("home_field.stopping_conditions")} htmlFor="rp-stop"><Input id="rp-stop" data-testid="replace-stop-input" value={stop} placeholder={task?.stopping_conditions} onChange={(e) => setStop(e.target.value)} /></Field>
    </ConfirmDialog>
  );
}

export function SendSection({ d, reload }) {
  const { t } = useTranslation();
  const prog = d.programme;
  const completed = d.visits.filter((v) => v.status === "completed");
  const sendable = d.home_tasks.filter((x) => ["draft", "sent"].includes(x.status));
  const [f, setF] = useState({ visit_id: completed[0]?.id || "", task_ids: sendable.filter((x) => x.status === "draft").map((x) => x.id), next_contact: "", message: "" });
  const [preview, setPreview] = useState(null);
  const [cid, setCid] = useState(newClientId());
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const blocks = preview?.recipients.flatMap((r) => r.blocks) || [];
  const open = async () => {
    setErr(null);
    try { const { data } = await api.post(`/programmes/${prog.id}/send-preview`, { ...f, visit_id: f.visit_id || null }); setPreview(data); }
    catch (e) { setErr(parseError(e)); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/programmes/${prog.id}/send`, { ...f, visit_id: f.visit_id || null, client_id: cid });
      if (data.instructions?.some((i) => i.email_status === "simulated")) showSimulated(data.instructions.map((i) => i.preview));
      else if (data.email_failed) toast.warning(t("send.email_failed"), { duration: 8000 }); else toast.success(t("send.sent"));
      setPreview(null);
      setCid(newClientId());
      reload();
    } catch (e) { setErr(parseError(e)); setPreview(null); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid="send-section">
      <h2 className="mb-3 text-lg font-bold">{t("send.title")}</h2>
      {d.owners.length === 0 ? <Notice tone="warn" testid="no-owner-linked">{t("err.no_owner_linked")}</Notice> : <>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("send.visit_report")} htmlFor="s-v"><Select id="s-v" data-testid="send-visit-select" value={f.visit_id} onChange={(e) => setF({ ...f, visit_id: e.target.value })}>
            <option value="">{t("send.no_report")}</option>{completed.map((v) => <option key={v.id} value={v.id}>{v.number} · {fmtDate(v.actual_date, v.actual_date_precision)}</option>)}</Select></Field>
          <Field label={t("send.next_contact")} htmlFor="s-nc"><Input id="s-nc" data-testid="send-next-contact-input" value={f.next_contact} onChange={(e) => setF({ ...f, next_contact: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label={t("send.message")} htmlFor="s-m" hint={t("send.message_hint")}><Textarea id="s-m" data-testid="send-message-input" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></Field></div>
        </div>
        <fieldset className="mt-3"><legend className="mb-1 text-sm font-semibold">{t("send.tasks")}</legend>
          {sendable.map((x) => <label key={x.id} className="flex min-h-[44px] items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5" data-testid={`send-task-checkbox-${x.number}`} checked={f.task_ids.includes(x.id)} onChange={() => setF({ ...f, task_ids: f.task_ids.includes(x.id) ? f.task_ids.filter((y) => y !== x.id) : [...f.task_ids, x.id] })} />{x.number} v{x.version} · {x.title} <StatusChip status={x.status} /></label>)}
        </fieldset>
        {err && <div className="mt-3"><Notice tone="warn" testid="send-error">{err.code === "send_paused" ? err.blocks.map((b, i) => <p key={i}>{t("send.no_edition", { lang: t(`lang_name.${b.language}`) })} ({b.task_number})</p>) : t(`err.${err.code}`)}</Notice></div>}
        <Btn className="mt-3" testid="send-to-owner-btn" onClick={open}>{t("send.cta")}</Btn>
      </>}
      {d.instructions.length > 0 && <>
        <h3 className="mb-2 mt-6 font-bold">{t("send.sent_editions")}</h3>
        <ul className="space-y-2" data-testid="sent-instructions">{d.instructions.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
            <b>{i.number}</b><span>{t("common.version_n", { n: i.version })}</span><Tag>{t(`lang.${i.language}`)}</Tag><span>{i.recipient_email}</span><span className="text-muted-foreground">{fmtDateTime(i.sent_at)} · {i.sender_name}</span>
            <StatusChip status={i.email_status === "sent" ? "sent" : "draft_outcome"} label={t(`send.estatus_${i.email_status}`)} />
            {i.possibly_affected && <StatusChip status="open" label={t("send.possibly_affected")} testid="instruction-possibly-affected" />}
          </li>))}</ul>
      </>}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent data-testid="send-preview-dialog" className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("send.preview_title")}</DialogTitle></DialogHeader>
          {preview?.recipients.map((r) => <PreviewBlock key={r.grant_id} r={r} />)}
          <Notice testid="send-excluded">{t("send.excluded")}</Notice>
          {blocks.length > 0 && <Notice tone="warn" testid="send-paused">{blocks.map((b, i) => <p key={i}>{t("send.no_edition", { lang: t(`lang_name.${b.language}`) })} ({b.task_number})</p>)}</Notice>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Btn variant="ghost" onClick={() => setPreview(null)}>{t("common.back")}</Btn>
            <Btn disabled={busy || blocks.length > 0} testid="send-confirm-btn" onClick={send}>{t("send.confirm")}</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PreviewBlock({ r }) {
  const { t } = useTranslation();
  const c = r.content;
  return (
    <div className="space-y-2 rounded-2xl border bg-white p-4 text-sm" data-testid="send-preview-recipient">
      <p><b>{t("confirm.recipient")}:</b> {r.name} · {r.email}</p>
      <p data-testid="send-preview-language"><b>{t("send.language")}:</b> {t(`lang_name.${r.language}`)}</p>
      {c.visit_report && <p><b>{t("send.visit_report")}:</b> {c.visit_report.number} — {t("send.done")}: {c.visit_report.completed.join(", ") || "—"}; {t("send.not_done")}: {c.visit_report.not_completed.map((x) => `${x.title} (${x.reason || t(`status.${x.outcome}`)})`).join(", ") || "—"}</p>}
      {c.tasks.map((x) => (
        <div key={x.task_id} className="rounded-xl bg-muted p-3">
          <p className="font-semibold">{x.number} v{x.version} · {x.title}</p>
          <p lang={x.language}>{x.text}</p>
          <p className="text-xs text-muted-foreground">{x.standard_block ? t("send.standard_block_lang", { lang: t(`lang_name.${x.language}`) }) : t("send.original_lang", { lang: t(`lang_name.${x.language}`) })}</p>
        </div>
      ))}
      {c.next_contact && <p><b>{t("send.next_contact")}:</b> {c.next_contact}</p>}
      {c.message && <p><b>{t("send.message")}:</b> {c.message}</p>}
    </div>
  );
}

export function ApplicationsSection({ apps }) {
  const { t } = useTranslation();
  return (
    <Card data-testid="methodology-use">
      <h2 className="mb-1 text-lg font-bold">{t("apps.title")}</h2>
      <p className="mb-3 text-sm text-muted-foreground">{t("apps.sub")}</p>
      {apps.length === 0 ? <p className="text-sm text-muted-foreground">{t("apps.none")}</p> : (
        <ul className="space-y-2">{apps.map((a) => (
          <li key={a.id} data-testid={`application-${a.number}`} className="rounded-xl border bg-white p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <b>{a.number}</b><Tag>{a.element_number} · {t("common.version_n", { n: a.element_version })}</Tag><StatusChip status={a.status === "active" ? a.outcome : "superseded"} label={a.status !== "active" ? t("status.superseded") : undefined} />
              <Tag>{t(`apps.${a.timing}`)}</Tag>{a.excluded_from_analytics && <Tag data-testid="application-excluded" className="bg-[hsl(42_95%_90%)] text-[hsl(30_80%_24%)]">{t("apps.excluded")}: {a.exclusion_reasons.map((r) => t(`apps.reason_${r}`)).join(", ")}</Tag>}
              {a.mentor_assisted && <Tag data-testid="application-mentor">{t("apps.mentor")}</Tag>}
            </div>
            <p className="mt-1">{a.actual_action}</p>
            <p className="text-xs text-muted-foreground">{t("apps.meta", { visit: a.visit_number, edition: a.programme_edition, performer: a.performer_name, date: fmtDate(a.actual_event_date, a.date_precision), entry: fmtDateTime(a.entry_date) })}</p>
            {a.assistance?.type !== "none" && <p className="text-xs">{t("visit.assistance")}: {t(`assist.${a.assistance.type}`)} — {a.assistance.name}</p>}
            {a.supporting_observations?.length > 0 && <p className="text-xs">{t("apps.observations")}: {a.supporting_observations.map((o) => `${o.number} (${t(`source.${o.source}`)})`).join(", ")}</p>}
          </li>))}</ul>
      )}
    </Card>
  );
}
